import DashboardLayout from 'examples/LayoutContainers/DashboardLayout';
import DashboardNavbar from 'examples/Navbars/DashboardNavbar';
import React, {useCallback, useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {Axios} from 'services/axios';
import OutlinedInput from '@mui/material/OutlinedInput';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Select from '@mui/material/Select';
import MDButton from 'components/MDButton';
import MDTypography from 'components/MDTypography';
import DataTable from 'examples/Tables/DataTable';
import moment from 'moment';
import MDBadge from 'components/MDBadge';
import MDBox from 'components/MDBox';
import {Confirm, Loading, Notify} from 'notiflix';
import {Typography, Box} from '@mui/material';
import usePermission from 'hooks/usePermission';
import NoPermissionNotice from 'components/NoPermissionNotice';

const IS_PRODUCTION = process.env.REACT_APP_IS_PRODUCTION === '1';
const IS_STAGE = process.env.REACT_APP_IS_STAGE === '1';
const FRONTEND_BASE = IS_PRODUCTION
  ? 'https://tecnogex.com'
  : IS_STAGE
  ? 'https://dev.tecnogex.com'
  : 'http://localhost:5173';

const columns = [
  {Header: 'Serial', accessor: 'sr', width: '5%', align: 'left'},
  {Header: 'Name', accessor: 'title', width: '20%', align: 'left'},
  {Header: 'Categories', accessor: 'categories', width: '35%', align: 'left'},
  {Header: 'Image', accessor: 'image', align: 'center', width: '10%'},
  {Header: 'Created at', accessor: 'createdOn', align: 'center'},
  {Header: 'Action', accessor: 'action', align: 'center'},
];

const CaseStudies = () => {
  const navigate = useNavigate();
  const [caseStudies, setCaseStudies] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedCatObj, setSelectedCatObj] = useState(null);
  const [filteredCaseStudies, setFilteredCaseStudies] = useState([]);
  const canRead = usePermission('case-studies', 'read');
  const canCreate = usePermission('case-studies', 'create');
  const canUpdate = usePermission('case-studies', 'update');
  const canDelete = usePermission('case-studies', 'delete');

  const handleDeleteCaseStudy = useCallback(id => {
    Confirm.show(
      'Confirm Deletion',
      'Are you sure you want to delete this case study?',
      'Yes', 'No',
      function () {
        Loading.circle('Deleting Case Study');
        const caseStudy = caseStudies.find(cs => cs.id === id);
        Axios.delete(`/shop/caseStudies/${id}/delete?label=${encodeURIComponent(caseStudy?.title || '')}`)
          .then(res => {
            Notify.success(res.data.message || 'Case study deleted successfully');
            setCaseStudies(prev => {
              const updated = prev.filter(cs => cs.id !== id);
              setFilteredCaseStudies(
                selectedCatObj ? updated.filter(cs => cs.categoryId === selectedCategory) : updated,
              );
              return updated;
            });
          })
          .catch(err => Notify.failure(err.response?.data?.message || 'Failed to delete case study'))
          .finally(() => Loading.remove());
      },
      function () {},
      {width: '320px', borderRadius: '8px', backgroundColor: '#fff'},
    );
  }, [selectedCatObj, selectedCategory]);

  const getRow = (caseStudy, idx) => ({
    sr: <MDTypography variant="caption">{idx + 1}</MDTypography>,
    title: <MDTypography variant="button" fontWeight="medium">{caseStudy.title}</MDTypography>,
    categories: (
      <Box display="flex" flexWrap="wrap" gap={0.5}>
        {(() => {
          const names = Array.isArray(caseStudy.sections) && caseStudy.sections.length > 0
            ? caseStudy.sections.map(s => s.category_name).filter(Boolean)
            : (() => { const c = categoriesList.find(c => c.id === caseStudy.categoryId); return c ? [c.name] : []; })();
          return names.length > 0
            ? names.map((name, i) => <MDBadge key={i} badgeContent={name} color="info" variant="gradient" size="sm" />)
            : <MDTypography variant="caption" color="textSecondary">—</MDTypography>;
        })()}
      </Box>
    ),
    image: <Box component="img" sx={{height: 40, width: 'auto'}} alt="Image" src={caseStudy.image_url || caseStudy.image} />,
    createdOn: <MDTypography variant="caption">{moment(caseStudy.createdAt).calendar()}</MDTypography>,
    action: (
      <MDBox ml={-1} display="flex" gap={1}>
        <MDBadge badgeContent="Copy URL" color="info" variant="gradient" size="sm"
          onClick={() => { navigator.clipboard.writeText(`${FRONTEND_BASE}/case-studies/${caseStudy.slug}`); Notify.success('URL copied!'); }}
          style={{cursor: 'pointer'}} />
        {canUpdate && (
          <MDBadge badgeContent="Edit" color="success" variant="gradient" size="sm"
            onClick={() => navigate(`/case-studies/edit/${caseStudy.id}`)}
            style={{cursor: 'pointer'}} />
        )}
        {canDelete && (
          <MDBadge badgeContent="Delete" color="error" variant="gradient" size="sm"
            onClick={() => handleDeleteCaseStudy(caseStudy.id)}
            style={{cursor: 'pointer'}} />
        )}
      </MDBox>
    ),
  });

  const fetchAllCaseStudies = async () => {
    if (!canRead) return;
    Loading.circle('Fetching Case Studies');
    try {
      const response = await Axios.fetch('/shop/getAllCaseStudies');
      const data = response.data.data || [];
      setCaseStudies(data);
      setFilteredCaseStudies(selectedCatObj ? data.filter(cs => cs.categoryId === selectedCategory) : data);
    } catch (err) {
      console.error('Error fetching case studies:', err);
    } finally {
      Loading.remove();
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await Axios.fetch('/shop/getAllActiveCategories');
      if (response?.data?.data) setCategoriesList(response.data.data);
    } catch (err) {
      console.error('Error fetching categories:', err);
    }
  };

  const handleFilterByCategory = () => {
    if (!selectedCategory) {
      setSelectedCatObj(null);
      setFilteredCaseStudies(caseStudies);
      return;
    }
    const catObj = categoriesList.find(cat => cat.id === selectedCategory);
    setSelectedCatObj(catObj || null);
    setFilteredCaseStudies(caseStudies.filter(cs => cs.categoryId === selectedCategory));
  };

  useEffect(() => {
    if (!canRead) return;
    fetchAllCaseStudies();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canRead]);

  if (!canRead) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <NoPermissionNotice message="Your role doesn't include read access to Case Studies. Ask an administrator to update your permissions." />
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div>
        <MDBox sx={{display: 'flex', justifyContent: 'flex-start', alignItems: 'center', gap: '10px', mt: 3}}>
          <FormControl sx={{width: 500}}>
            <InputLabel id="cs-cat-label">Select Category</InputLabel>
            <Select labelId="cs-cat-label" sx={{height: 45}} value={selectedCategory}
              onChange={e => setSelectedCategory(e.target.value)}
              input={<OutlinedInput label="Select Category" />}>
              {categoriesList.map(cat => (<MenuItem key={cat.id} value={cat.id}>{cat.name}</MenuItem>))}
            </Select>
          </FormControl>
          <MDButton sx={{width: 100, height: 45}} onClick={handleFilterByCategory} variant="contained" color="info">Submit</MDButton>
          {selectedCatObj && (
            <MDButton sx={{height: 45}} onClick={() => {setSelectedCategory(''); setSelectedCatObj(null); setFilteredCaseStudies(caseStudies);}} variant="outlined" color="secondary">Clear</MDButton>
          )}
        </MDBox>

        <MDBox mt={4}>
          <MDBox mb={2} sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
            <h4>{selectedCatObj ? selectedCatObj.name : 'All Case Studies'}</h4>
            {canCreate && (
              <MDButton variant="contained" color="info" onClick={() => navigate('/case-studies/new')}>
                Add New Case Study
              </MDButton>
            )}
          </MDBox>
          {filteredCaseStudies.length > 0 && (
            <DataTable table={{columns, rows: filteredCaseStudies.map((row, idx) => getRow(row, idx))}}
              isSorted={false} entriesPerPage={false} showTotalEntries={false} noEndBorder hoverEffect />
          )}
          {filteredCaseStudies.length === 0 && (
            <Typography variant="h6" align="center" mt={4} color="textSecondary">
              {selectedCatObj ? `No Case Studies in "${selectedCatObj.name}"` : 'No Case Studies Found'}
            </Typography>
          )}
        </MDBox>
      </div>
    </DashboardLayout>
  );
};

export default CaseStudies;
